import { useCallback, useEffect } from 'react';

import { omit } from '@revenuemarket/utils';
import { useRouter } from 'next/router';

import { usePersistState } from '../usePersistState';
import { useQueryParam } from '../useQueryParam';

type NumericString = `${number}`;
type NumericStringToNumber<T> = T extends `${infer U extends number}` ? U : never;

export type StepModel<T extends readonly [unknown | undefined, ...unknown[]]> = {
  [K in Extract<keyof T, NumericString>]: {
    step: NumericStringToNumber<K>;
    model: T[K];
  };
}[Extract<keyof T, NumericString>];

const defaultStepModel = {
  step: 0,
  model: undefined,
};

interface Props<T extends readonly [unknown | undefined, ...unknown[]]> {
  name: string;
  defaultValue?: StepModel<T>;

  // resetStep시 router를 조작하는 경우 사이드이펙트가 발생하는 경우가 있어 resetStep 호출시 router.replace 를 호출하지 않도록 할 수 있도록 하는 옵션
  skipRouterReplaceOnResetStep?: boolean;
}

export function useSteps<T extends readonly [unknown | undefined, ...unknown[]]>({
  defaultValue,
  name,
  skipRouterReplaceOnResetStep = false,
}: Props<T>) {
  const currentStep = useQueryParam('step', { parser: Number }) ?? 0;
  const router = useRouter();
  const [step, setStep] = usePersistState<StepModel<T>>({
    name,
    defaultValue: {
      ...(defaultValue ?? defaultStepModel),
      step: currentStep,
    } as StepModel<T>,
  });

  const moveStep = useCallback(
    (value: StepModel<T>) => {
      setStep(value);
      router.replace({ query: { ...router.query, step: value.step } });
    },
    [router, setStep],
  );

  const resetStep = useCallback(async () => {
    setStep((defaultValue ?? defaultStepModel) as StepModel<T>);

    if (!skipRouterReplaceOnResetStep) {
      await router.replace({ query: omit(router.query, ['step']) });
    }
  }, [defaultValue, router, setStep, skipRouterReplaceOnResetStep]);

  useEffect(() => {
    return () => {
      resetStep();
    };
  }, []);

  return [step, moveStep, resetStep] as const;
}
